export const sanitizeDocument = (value = '') =>
  String(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const isOnlyDigits = (value = '') => /^\d+$/.test(value);

const formatCpf = (value = '') => {
  const sanitized = sanitizeDocument(value).slice(0, 11);
  if (!sanitized) return '';
  const blocks = [
    sanitized.slice(0, 3),
    sanitized.slice(3, 6),
    sanitized.slice(6, 9),
    sanitized.slice(9, 11),
  ].filter(Boolean);
  if (blocks.length <= 1) return blocks[0] || '';
  if (blocks.length === 2) return `${blocks[0]}.${blocks[1]}`;
  if (blocks.length === 3) return `${blocks[0]}.${blocks[1]}.${blocks[2]}`;
  return `${blocks[0]}.${blocks[1]}.${blocks[2]}-${blocks[3]}`;
};

const formatCnpj = (value = '') => {
  const sanitized = sanitizeDocument(value).slice(0, 14);
  if (!sanitized) return '';
  const blocks = [
    sanitized.slice(0, 2),
    sanitized.slice(2, 5),
    sanitized.slice(5, 8),
    sanitized.slice(8, 12),
    sanitized.slice(12, 14),
  ].filter(Boolean);
  if (blocks.length <= 1) return blocks[0] || '';
  if (blocks.length === 2) return `${blocks[0]}.${blocks[1]}`;
  if (blocks.length === 3) return `${blocks[0]}.${blocks[1]}.${blocks[2]}`;
  if (blocks.length === 4) {
    return `${blocks[0]}.${blocks[1]}.${blocks[2]}/${blocks[3]}`;
  }
  return `${blocks[0]}.${blocks[1]}.${blocks[2]}/${blocks[3]}-${blocks[4]}`;
};

export const getDocumentType = (value = '') => {
  const sanitized = sanitizeDocument(value);
  if (sanitized.length === 11 && isOnlyDigits(sanitized)) return 'CPF';
  if (sanitized.length === 14 && isOnlyDigits(sanitized)) return 'CNPJ';
  if (sanitized.length === 14) return 'CNPJ_ALPHANUMERIC';
  return 'UNKNOWN';
};

export const formatDocumentForDisplay = (value = '') => {
  const sanitized = sanitizeDocument(value);
  const type = getDocumentType(sanitized);
  if (type === 'CPF' || (sanitized.length > 0 && sanitized.length <= 11)) {
    return formatCpf(sanitized);
  }
  return formatCnpj(sanitized);
};

export const normalizeDocumentForPayload = (value = '') =>
  sanitizeDocument(value);

const validateCPF = (strCPF = '') => {
  const newCpf = String(strCPF).replace(/[^\d]/g, '');
  let retSum = 0;
  let restOfDivision;
  if (newCpf === '' || newCpf.length !== 11) return false;
  if (
    newCpf === '00000000000' ||
    newCpf === '11111111111' ||
    newCpf === '22222222222' ||
    newCpf === '33333333333' ||
    newCpf === '44444444444' ||
    newCpf === '55555555555' ||
    newCpf === '66666666666' ||
    newCpf === '77777777777' ||
    newCpf === '88888888888' ||
    newCpf === '99999999999'
  )
    return false;
  for (let i = 1; i <= 9; i += 1) {
    retSum += Number(newCpf.substring(i - 1, i)) * (11 - i);
  }
  restOfDivision = (retSum * 10) % 11;
  if (restOfDivision === 10 || restOfDivision === 11) restOfDivision = 0;
  if (restOfDivision !== Number(newCpf.substring(9, 10))) return false;
  retSum = 0;
  for (let i = 1; i <= 10; i += 1) {
    retSum += Number(newCpf.substring(i - 1, i)) * (12 - i);
  }
  restOfDivision = (retSum * 10) % 11;
  if (restOfDivision === 10 || restOfDivision === 11) restOfDivision = 0;
  return restOfDivision === Number(newCpf.substring(10, 11));
};

const validateCNPJ = (cnpj = '') => {
  const sanitized = String(cnpj).replace(/[^\d]+/g, '');
  if (sanitized === '' || sanitized.length !== 14) return false;
  if (
    sanitized === '00000000000000' ||
    sanitized === '11111111111111' ||
    sanitized === '22222222222222' ||
    sanitized === '33333333333333' ||
    sanitized === '44444444444444' ||
    sanitized === '55555555555555' ||
    sanitized === '66666666666666' ||
    sanitized === '77777777777777' ||
    sanitized === '88888888888888' ||
    sanitized === '99999999999999'
  )
    return false;
  let tamanho = sanitized.length - 2;
  let numeros = sanitized.substring(0, tamanho);
  const digitos = sanitized.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i -= 1) {
    soma += Number(numeros.charAt(tamanho - i)) * pos;
    pos -= 1;
    if (pos < 2) pos = 9;
  }
  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado.toString() !== digitos.charAt(0)) return false;
  tamanho += 1;
  numeros = sanitized.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i -= 1) {
    soma += Number(numeros.charAt(tamanho - i)) * pos;
    pos -= 1;
    if (pos < 2) pos = 9;
  }
  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  return resultado.toString() === digitos.charAt(1);
};

export const validateDocument = (value = '') => {
  if (!value) return 'É obrigatório preencher seus dados!';
  const sanitized = sanitizeDocument(value);
  const type = getDocumentType(sanitized);
  if (sanitized.length > 0 && sanitized.length < 11) return 'Insira um CPF válido';
  if (sanitized.length > 11 && sanitized.length < 14) return 'Insira um CNPJ válido';
  if (type === 'CPF' && !validateCPF(sanitized)) return 'CPF inválido';
  if (type === 'CNPJ' && !validateCNPJ(sanitized)) return 'CNPJ inválido';
  if (type === 'CNPJ_ALPHANUMERIC') return '';
  if (sanitized.length > 14) return 'Insira um CNPJ válido';
  return '';
};
